import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, Trash2, Pencil, Search, FileText, Check, X, Undo2 } from 'lucide-react';
import api from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import { puoFare } from '../../utils/permessi';
import { DialogoHost, conferma, avvisa } from '../../components/Dialogo';
import InFinestra from '../../components/InFinestra';
import AnteprimaDocumento from '../../components/AnteprimaDocumento';
import RicevuteModello from '../planning/RicevuteModello';

// Ricevute istituzionali ad altre ASD (23/10/2026, titolare): elenco delle associazioni con i loro dati,
// dentro ognuna le sue ricevute fatte di righe libere (descrizione + importo).
// Bozza (si modifica) → anteprima → "Conferma e genera" (numero ASD-0001/2026, righe bloccate) → pagata.
// La dicitura IVA si sceglie per ricevuta tra i regimi (scheda Regimi IVA) e resta copiata sulla ricevuta.
// Indirizzi: /ricevute-asd · /ricevute-asd/<id associazione> · /ricevute-asd/regimi · /ricevute-asd/modello
const euro = (n) => `€ ${(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const errore = (e, def) => e?.response?.data?.error || def;
const gg = (d) => (d ? new Date(d).toLocaleDateString('it-IT', { timeZone: 'Europe/Rome' }) : '');
const METODI = [['BONIFICO', 'Bonifico'], ['CONTANTI', 'Contanti'], ['SATISPAY', 'Satispay'], ['POS', 'Carta / POS'], ['ALTRO', 'Altro']];
const nomeMetodo = (m) => (METODI.find(x => x[0] === m) || [m, m])[1];

export default function RicevuteAsd() {
  const { user } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const parte = loc.pathname.replace(/^\/ricevute-asd\/?/, '').split('/')[0];
  if (!puoFare(user, 'ricevute_asd', 'view')) return <div style={s.pagina}><div style={s.vuoto}>Serve il permesso Ricevute ad altre ASD (Impostazioni → Ruoli).</div></div>;
  const modello = parte === 'modello', regimi = parte === 'regimi';
  return (
    <div style={s.pagina}>
      <DialogoHost />
      <h1 style={s.titolo}>Ricevute ad altre ASD</h1>
      <div style={s.sub}>Ricevute istituzionali verso altre associazioni sportive. Per ogni associazione i suoi dati e le sue ricevute, con le righe che scrivi tu e il regime IVA scelto di volta in volta.</div>
      <div style={s.schede}>
        <button style={{ ...s.scheda, ...(!modello && !regimi ? s.schedaOn : {}) }} onClick={() => nav('/ricevute-asd')}>Associazioni</button>
        <button style={{ ...s.scheda, ...(regimi ? s.schedaOn : {}) }} onClick={() => nav('/ricevute-asd/regimi')}>Regimi IVA</button>
        <button style={{ ...s.scheda, ...(modello ? s.schedaOn : {}) }} onClick={() => nav('/ricevute-asd/modello')}>Modello ricevuta</button>
      </div>
      {modello
        ? <RicevuteModello base="/ricevute-asd" puoModificare={puoFare(user, 'ricevute_asd', 'edit')} puoEliminare={puoFare(user, 'ricevute_asd', 'delete')}
            spiegazione={<>
              <p style={s.p}>Le ricevute ad altre associazioni nascono da un file <b>Word (.docx)</b> a parte, diverso da quello dei corsi, con i dati tra doppie graffe (es. <code>{'{{dest_nome}}'}</code>, <code>{'{{totale}}'}</code>).</p>
              <p style={s.p}>Le righe si ripetono con <code>{'{{#righe}}'}</code> … <code>{'{{/righe}}'}</code>: in una tabella, la prima cella della riga comincia con <code>{'{{#righe}}'}</code> e l'ultima finisce con <code>{'{{/righe}}'}</code>, dentro <code>{'{{descrizione}}'}</code> e <code>{'{{importo}}'}</code>.</p>
            </>} />
        : regimi ? <Regimi />
        : parte ? <Dettaglio id={+parte} nav={nav} /> : <Elenco nav={nav} />}
    </div>
  );
}

// ─── Elenco associazioni ───────────────────────────────────────────────────────────────
function Elenco({ nav }) {
  const { user } = useAuth();
  const [lista, setLista] = useState(null);
  const [q, setQ] = useState('');
  const [nuova, setNuova] = useState(false);
  const [err, setErr] = useState('');
  const carica = () => api.get('/ricevute-asd').then(r => setLista(r.data)).catch(e => setErr(errore(e, 'Non caricato')));
  useEffect(() => { carica(); }, []);
  const visibili = useMemo(() => {
    const parole = q.toLowerCase().split(/\s+/).filter(Boolean);
    return (lista || []).filter(a => { const t = [a.denominazione, a.citta, a.codice_fiscale, a.federazione, a.referente].join(' ').toLowerCase(); return parole.every(p => t.includes(p)); });
  }, [lista, q]);
  const daIncassare = (lista || []).reduce((t, a) => t + a.da_incassare, 0);
  return (
    <>
      {err && <div style={s.err}>{err}</div>}
      <div style={s.barra}>
        <div style={s.cerca}><Search size={15} /><input style={s.cercaInput} value={q} onChange={e => setQ(e.target.value)} placeholder="Cerca associazione, città, codice fiscale…" /></div>
        {puoFare(user, 'ricevute_asd', 'create') && <button style={s.btnPrimario} onClick={() => setNuova(true)}><Plus size={15} /> Nuova associazione</button>}
      </div>
      {daIncassare > 0 && <div style={{ ...s.banner, ...s.bannerAvviso }}>Da incassare su ricevute già emesse: <b>{euro(daIncassare)}</b></div>}
      {!lista ? <div style={s.vuoto}>Caricamento…</div>
        : !lista.length ? <div style={s.nessuna}><div style={{ fontWeight: 700 }}>Nessuna associazione</div><div style={s.dim}>Aggiungi la prima associazione a cui fare una ricevuta.</div></div>
        : <div style={s.griglia}>
          {visibili.map(a => (
            <button key={a.id} style={s.card} onClick={() => nav(`/ricevute-asd/${a.id}`)}>
              <div style={{ fontSize: 17, fontWeight: 800 }}>{a.denominazione}</div>
              <div style={s.dim}>{[a.citta, a.federazione].filter(Boolean).join(' · ') || '—'}</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 'auto' }}>
                <span style={{ ...s.tag, ...s.tagGrigio }}>{a.emesse} {a.emesse === 1 ? 'ricevuta' : 'ricevute'} · {euro(a.totale_emesso)}</span>
                {a.da_incassare > 0 && <span style={{ ...s.tag, ...s.tagArancio }}>da incassare {euro(a.da_incassare)}</span>}
                {a.bozze > 0 && <span style={{ ...s.tag, ...s.tagBlu }}>{a.bozze} {a.bozze === 1 ? 'bozza' : 'bozze'}</span>}
              </div>
            </button>
          ))}
          {!visibili.length && <div style={s.vuoto}>Nessuna associazione trovata.</div>}
        </div>}
      {nuova && <InFinestra attiva onChiudi={() => setNuova(false)} larghezza={720}>
        <SchedaAsd onChiudi={() => setNuova(false)} onSalvata={(a) => { setNuova(false); nav(`/ricevute-asd/${a.id}`); }} />
      </InFinestra>}
    </>
  );
}

// ─── Dati dell'associazione (nuova o modifica) ─────────────────────────────────────────
const CAMPI = [
  ['denominazione', 'Denominazione *', 2], ['codice_fiscale', 'Codice fiscale'], ['partita_iva', 'Partita IVA'],
  ['indirizzo', 'Indirizzo sede', 2], ['cap', 'CAP'], ['citta', 'Città'], ['provincia', 'Provincia (es. LC)'],
  ['federazione', 'Federazione / ente (es. FIPAV)'], ['codice_affiliazione', 'Codice affiliazione'],
  ['referente', 'Referente'], ['telefono', 'Telefono'], ['email', 'Email'], ['pec', 'PEC'], ['note', 'Note interne', 2],
];
function SchedaAsd({ asd, onChiudi, onSalvata }) {
  const [f, setF] = useState(() => Object.fromEntries(CAMPI.map(([k]) => [k, asd?.[k] || ''])));
  const [err, setErr] = useState('');
  const [invio, setInvio] = useState(false);
  const salva = async (e) => {
    e.preventDefault(); setErr(''); setInvio(true);
    try { const r = asd ? await api.put(`/ricevute-asd/${asd.id}`, f) : await api.post('/ricevute-asd', f); onSalvata(r.data); }
    catch (x) { setErr(errore(x, 'Non salvata')); } finally { setInvio(false); }
  };
  return (
    <form style={s.finestra} onSubmit={salva}>
      <div style={s.testaFin}><div style={{ fontSize: 18, fontWeight: 800 }}>{asd ? 'Dati dell\'associazione' : 'Nuova associazione'}</div><button type="button" style={s.icona} onClick={onChiudi}><X size={18} /></button></div>
      <div style={s.campi}>
        {CAMPI.map(([k, l, col]) => (
          <label key={k} style={{ ...s.campo, gridColumn: col === 2 ? '1 / -1' : undefined }}>
            <span style={s.etichetta}>{l}</span>
            {k === 'note' ? <textarea style={{ ...s.input, minHeight: 60 }} value={f[k]} onChange={e => setF({ ...f, [k]: e.target.value })} />
              : <input style={s.input} value={f[k]} onChange={e => setF({ ...f, [k]: e.target.value })} />}
          </label>
        ))}
      </div>
      <div style={s.dim}>I dati compaiono nell'intestazione della ricevuta: se li cambi, anche le ricevute già emesse si ristampano con i dati nuovi.</div>
      {err && <div style={s.err}>{err}</div>}
      <div style={s.piede}><button type="button" style={s.btn} onClick={onChiudi}>Annulla</button><button style={s.btnPrimario} disabled={invio}>{invio ? 'Salvo…' : 'Salva'}</button></div>
    </form>
  );
}

// ─── Dettaglio: dati + ricevute ────────────────────────────────────────────────────────
function Dettaglio({ id, nav }) {
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [err, setErr] = useState('');
  const [modDati, setModDati] = useState(false);
  const [editor, setEditor] = useState(null); // null | {} nuova | ricevuta bozza
  const [aperta, setAperta] = useState(null);  // ricevuta in anteprima
  const [pagamento, setPagamento] = useState(null);
  const carica = () => api.get(`/ricevute-asd/${id}`).then(r => { setA(r.data); return r.data; }).catch(e => setErr(errore(e, 'Non caricata')));
  useEffect(() => { carica(); }, [id]); // eslint-disable-line
  const puo = (az) => puoFare(user, 'ricevute_asd', az);
  if (err && !a) return <div style={s.err}>{err}</div>;
  if (!a) return <div style={s.vuoto}>Caricamento…</div>;

  const eliminaAsd = async () => {
    if (!(await conferma(`Eliminare "${a.denominazione}"?${a.ricevute.length ? ' Le sue bozze si cancellano con lei.' : ''}`, { si: 'Elimina', pericolo: true }))) return;
    try { await api.delete(`/ricevute-asd/${id}`); nav('/ricevute-asd'); } catch (e) { avvisa(errore(e, 'Non eliminata')); }
  };
  const eliminaBozza = async (r) => {
    if (!(await conferma('Eliminare questa bozza?', { si: 'Elimina', pericolo: true }))) return;
    try { await api.delete(`/ricevute-asd/ricevute/${r.id}`); carica(); } catch (e) { avvisa(errore(e, 'Non eliminata')); }
  };
  const emetti = async () => {
    if (!(await conferma(`Confermare la ricevuta di ${euro(aperta.totale)}?\nRiceve il numero progressivo e le righe non si potranno più cambiare.`, { si: 'Conferma e genera' }))) return;
    try { const r = await api.post(`/ricevute-asd/ricevute/${aperta.id}/emetti`); setAperta(r.data); carica(); } catch (e) { avvisa(errore(e, 'Non confermata')); }
  };
  const nonPagata = async (r) => {
    if (!(await conferma(`Segnare la ricevuta ${r.numero_testo} come NON pagata?`))) return;
    try { await api.put(`/ricevute-asd/ricevute/${r.id}/pagamento`, { pagata: false }); carica(); } catch (e) { avvisa(errore(e, 'Errore')); }
  };
  const indirizzo = [a.indirizzo, [a.cap, a.citta].filter(Boolean).join(' '), a.provincia && `(${a.provincia})`].filter(Boolean).join(', ').replace(', (', ' (');
  const bozza = aperta?.stato === 'BOZZA';

  return (
    <>
      <div style={s.briciole}><button style={s.link} onClick={() => nav('/ricevute-asd')}>Associazioni</button><span style={s.dim}> › </span><span style={{ fontWeight: 700, color: '#C24B08' }}>{a.denominazione}</span></div>
      <div style={s.colonne}>
        <div style={s.boxDati}>
          <div style={s.testaCol}><div style={s.etichetta}>DATI</div>
            <div style={{ display: 'flex', gap: 4 }}>
              {puo('edit') && <button style={s.icona} title="Modifica" onClick={() => setModDati(true)}><Pencil size={15} /></button>}
              {puo('delete') && !a.ricevute.some(r => r.stato === 'EMESSA') && <button style={{ ...s.icona, color: '#C0392B' }} title="Elimina associazione" onClick={eliminaAsd}><Trash2 size={15} /></button>}
            </div>
          </div>
          <div style={{ fontSize: 18, fontWeight: 800 }}>{a.denominazione}</div>
          {indirizzo && <div style={s.dim}>{indirizzo}</div>}
          <Riga l="C.F." v={a.codice_fiscale} /><Riga l="P.IVA" v={a.partita_iva} />
          <Riga l="Affiliazione" v={[a.federazione, a.codice_affiliazione].filter(Boolean).join(' · ')} />
          <Riga l="Referente" v={a.referente} /><Riga l="Telefono" v={a.telefono} /><Riga l="Email" v={a.email} /><Riga l="PEC" v={a.pec} />
          {a.note && <div style={{ ...s.dim, whiteSpace: 'pre-line', marginTop: 6 }}>{a.note}</div>}
          {!a.codice_fiscale && <div style={{ ...s.tag, ...s.tagArancio, alignSelf: 'flex-start', marginTop: 6 }}>manca il codice fiscale</div>}
        </div>

        <div style={s.boxRicevute}>
          <div style={s.testaCol}><div style={s.etichetta}>RICEVUTE</div>
            {puo('create') && <button style={s.btnPrimario} onClick={() => setEditor({})}><Plus size={15} /> Nuova ricevuta</button>}
          </div>
          {!a.ricevute.length && <div style={s.dim}>Nessuna ricevuta per questa associazione.</div>}
          {a.ricevute.map(r => (
            <div key={r.id} style={s.rigaRic}>
              <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                <div style={{ fontWeight: 700 }}>{r.stato === 'BOZZA' ? 'Bozza' : `N. ${r.numero_testo}`}{r.data_emissione && <span style={s.dim}> · {gg(r.data_emissione)}</span>}</div>
                <div style={s.dim}>{r.causale || (r.righe || []).map(x => x.descrizione).join(' · ')}</div>
                {r.regime_nome ? <div style={{ ...s.dim, fontSize: 12 }}>{r.regime_nome}{r.affiliazione_comune ? ` · ${r.affiliazione_comune}` : ''}</div>
                  : r.stato === 'BOZZA' && <div style={{ ...s.tag, ...s.tagArancio, display: 'inline-block', marginTop: 2 }}>manca il regime IVA</div>}
              </div>
              <div style={{ fontWeight: 800, whiteSpace: 'nowrap' }}>{euro(r.totale)}</div>
              {r.stato === 'BOZZA' ? <span style={{ ...s.tag, ...s.tagBlu }}>BOZZA</span>
                : r.pagata ? <span style={{ ...s.tag, ...s.tagVerde }} title={`${nomeMetodo(r.metodo_pagamento)} il ${gg(r.data_pagamento)}`}>PAGATA {gg(r.data_pagamento)}</span>
                : <span style={{ ...s.tag, ...s.tagArancio }}>NON PAGATA</span>}
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                <button style={s.btn} onClick={() => setAperta(r)}><FileText size={14} /> {r.stato === 'BOZZA' ? 'Anteprima' : 'PDF'}</button>
                {r.stato === 'BOZZA' && puo('edit') && <button style={s.icona} title="Modifica bozza" onClick={() => setEditor(r)}><Pencil size={15} /></button>}
                {r.stato === 'BOZZA' && puo('delete') && <button style={{ ...s.icona, color: '#C0392B' }} title="Elimina bozza" onClick={() => eliminaBozza(r)}><Trash2 size={15} /></button>}
                {r.stato === 'EMESSA' && puo('edit') && (r.pagata
                  ? <button style={s.icona} title="Segna non pagata" onClick={() => nonPagata(r)}><Undo2 size={15} /></button>
                  : <button style={s.btn} onClick={() => setPagamento(r)}><Check size={14} /> Pagata</button>)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {modDati && <InFinestra attiva onChiudi={() => setModDati(false)} larghezza={720}>
        <SchedaAsd asd={a} onChiudi={() => setModDati(false)} onSalvata={() => { setModDati(false); carica(); }} />
      </InFinestra>}
      {editor && <InFinestra attiva onChiudi={() => setEditor(null)} larghezza={760}>
        <EditorRicevuta asd={a} ricevuta={editor.id ? editor : null} onChiudi={() => setEditor(null)}
          onSalvata={(r) => { setEditor(null); carica(); setAperta(r); }} />
      </InFinestra>}
      {pagamento && <InFinestra attiva onChiudi={() => setPagamento(null)} larghezza={460}>
        <SegnaPagata r={pagamento} onChiudi={() => setPagamento(null)} onFatto={() => { setPagamento(null); carica(); }} />
      </InFinestra>}
      {aperta && (
        <AnteprimaDocumento key={`${aperta.id}-${aperta.stato}`}
          doc={{ nome: bozza ? `Anteprima ricevuta ${a.denominazione}` : `Ricevuta ${aperta.numero_testo} ${a.denominazione}`, file_nome: 'ricevuta.pdf', tipo: 'Ricevuta' }}
          endpoint={`/ricevute-asd/ricevute/${aperta.id}/pdf`}
          avviso={bozza ? 'Anteprima: controlla i dati e premi "Conferma e genera" per avere la ricevuta con il suo numero.' : null}
          azioni={bozza && puo('create') ? <button style={{ ...s.btnPrimario, padding: '7px 12px' }} onClick={emetti}>CONFERMA E GENERA</button> : null}
          onClose={() => setAperta(null)} />
      )}
    </>
  );
}

const Riga = ({ l, v }) => (v ? <div style={{ fontSize: 14 }}><span style={s.dim}>{l} </span>{v}</div> : null);

// ─── Righe della ricevuta (bozza) ──────────────────────────────────────────────────────
function EditorRicevuta({ asd, ricevuta, onChiudi, onSalvata }) {
  const [causale, setCausale] = useState(ricevuta?.causale || '');
  const [regimi, setRegimi] = useState([]);
  const [regimeId, setRegimeId] = useState(ricevuta?.regime_id || '');
  const [affiliazione, setAffiliazione] = useState(ricevuta?.affiliazione_comune ?? (asd.federazione || ''));
  useEffect(() => {
    api.get('/ricevute-asd/regimi').then(r => {
      const attivi = r.data.filter(x => x.attivo || x.id === ricevuta?.regime_id);
      setRegimi(attivi);
      if (!ricevuta?.regime_id && attivi[0]) setRegimeId(attivi[0].id);
    }).catch(() => setRegimi([]));
  }, []); // eslint-disable-line
  const regime = regimi.find(x => x.id === +regimeId);
  const anteprimaIva = regime ? regime.testo.replace(/\{\{\s*affiliazione\s*\}\}/g, affiliazione.trim() || '…') : '';
  const [note, setNote] = useState(ricevuta?.note || '');
  const [righe, setRighe] = useState(() => (ricevuta?.righe?.length ? ricevuta.righe.map(r => ({ descrizione: r.descrizione, importo: String(r.importo).replace('.', ',') })) : [{ descrizione: '', importo: '' }]));
  const [err, setErr] = useState('');
  const [invio, setInvio] = useState(false);
  const num = (v) => Number(String(v).replace(/\./g, '').replace(',', '.')) || 0;
  const totale = righe.reduce((t, r) => t + num(r.importo), 0);
  const cambia = (i, k, v) => setRighe(righe.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const salva = async (e) => {
    e.preventDefault(); setErr(''); setInvio(true);
    const corpo = { causale, note, regime_id: +regimeId || null, affiliazione_comune: affiliazione, righe: righe.map(r => ({ descrizione: r.descrizione, importo: num(r.importo) })) };
    try {
      const r = ricevuta ? await api.put(`/ricevute-asd/ricevute/${ricevuta.id}`, corpo) : await api.post(`/ricevute-asd/${asd.id}/ricevute`, corpo);
      onSalvata(r.data);
    } catch (x) { setErr(errore(x, 'Non salvata')); } finally { setInvio(false); }
  };
  return (
    <form style={s.finestra} onSubmit={salva}>
      <div style={s.testaFin}><div style={{ fontSize: 18, fontWeight: 800 }}>{ricevuta ? 'Modifica bozza' : 'Nuova ricevuta'}</div><button type="button" style={s.icona} onClick={onChiudi}><X size={18} /></button></div>
      <label style={s.campo}><span style={s.etichetta}>OGGETTO (FACOLTATIVO)</span>
        <input style={s.input} value={causale} maxLength={200} onChange={e => setCausale(e.target.value)} placeholder="es. Tappa Serie Beach 1 – Robbiate 12/10/2026" /></label>
      <div style={s.etichetta}>RIGHE</div>
      {righe.map((r, i) => (
        <div key={i} style={s.rigaEdit}>
          <input style={{ ...s.input, flex: '1 1 260px' }} value={r.descrizione} maxLength={300} placeholder="Descrizione" onChange={e => cambia(i, 'descrizione', e.target.value)} />
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flex: '0 0 auto' }}>
            <span style={s.dim}>€</span>
            <input style={{ ...s.input, width: 110, textAlign: 'right' }} inputMode="decimal" value={r.importo} placeholder="0,00" onChange={e => cambia(i, 'importo', e.target.value)} />
            <button type="button" style={{ ...s.icona, visibility: righe.length > 1 ? 'visible' : 'hidden' }} title="Togli riga" onClick={() => setRighe(righe.filter((_, j) => j !== i))}><Trash2 size={15} /></button>
          </div>
        </div>
      ))}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <button type="button" style={s.link} onClick={() => setRighe([...righe, { descrizione: '', importo: '' }])}><Plus size={14} /> Aggiungi riga</button>
        <div style={{ fontSize: 16 }}>Totale <b style={{ color: '#C24B08' }}>{euro(totale)}</b></div>
      </div>
      <div style={s.rigaEdit}>
        <label style={{ ...s.campo, flex: '2 1 280px' }}><span style={s.etichetta}>REGIME IVA</span>
          <select style={s.input} value={regimeId} onChange={e => setRegimeId(e.target.value)}>
            <option value="">— scegli —</option>
            {regimi.map(x => <option key={x.id} value={x.id}>{x.nome}{x.attivo ? '' : ' (spento)'}</option>)}
          </select></label>
        {regime?.usa_affiliazione && <label style={{ ...s.campo, flex: '1 1 160px' }}><span style={s.etichetta}>AFFILIAZIONE IN COMUNE</span>
          <input style={s.input} value={affiliazione} maxLength={80} placeholder="es. FIPAV" onChange={e => setAffiliazione(e.target.value)} /></label>}
      </div>
      {regime && <div style={s.anteprimaIva}>{anteprimaIva}</div>}
      <label style={s.campo}><span style={s.etichetta}>NOTE IN FONDO ALLA RICEVUTA (FACOLTATIVE)</span>
        <textarea style={{ ...s.input, minHeight: 56 }} value={note} maxLength={1000} onChange={e => setNote(e.target.value)} placeholder="es. Da versare tramite bonifico entro 30 giorni" /></label>
      <div style={s.dim}>Si salva come bozza e si apre l'anteprima: il numero arriva solo con "Conferma e genera".</div>
      {err && <div style={s.err}>{err}</div>}
      <div style={s.piede}><button type="button" style={s.btn} onClick={onChiudi}>Annulla</button><button style={s.btnPrimario} disabled={invio}>{invio ? 'Salvo…' : 'Salva e guarda l\'anteprima'}</button></div>
    </form>
  );
}

// ─── Regimi IVA: nome breve + testo stampato ({{affiliazione}} = affiliazione comune scritta sulla ricevuta)
function Regimi() {
  const { user } = useAuth();
  const [lista, setLista] = useState(null);
  const [aperto, setAperto] = useState(null); // {} nuovo | regime
  const [err, setErr] = useState('');
  const carica = () => api.get('/ricevute-asd/regimi').then(r => setLista(r.data)).catch(e => setErr(errore(e, 'Non caricati')));
  useEffect(() => { carica(); }, []);
  const puoMod = puoFare(user, 'ricevute_asd', 'edit');
  const elimina = async (r) => {
    if (!(await conferma(`Eliminare il regime "${r.nome}"?\nSe è già stato usato su qualche ricevuta viene solo spento: quelle ricevute restano con il loro testo.`, { si: 'Elimina', pericolo: true }))) return;
    try { const x = await api.delete(`/ricevute-asd/regimi/${r.id}`); if (x.data.spento) avvisa('Già usato su qualche ricevuta: l\'ho spento, non compare più nella scelta.'); carica(); } catch (e) { avvisa(errore(e, 'Non eliminato')); }
  };
  const riaccendi = async (r) => { try { await api.put(`/ricevute-asd/regimi/${r.id}`, { ...r, attivo: true }); carica(); } catch (e) { avvisa(errore(e, 'Errore')); } };
  return (
    <>
      <div style={{ ...s.banner }}>
        <div>Ogni ricevuta stampa il testo del regime scelto. Nel testo puoi scrivere <code>{'{{affiliazione}}'}</code>: al suo posto va la federazione/ente in comune indicata sulla ricevuta (es. FIPAV). Cambiare un testo vale per le ricevute nuove e per le bozze che risalvi: quelle già salvate tengono il loro.</div>
      </div>
      {err && <div style={s.err}>{err}</div>}
      {puoMod && <div style={{ marginBottom: 12 }}><button style={s.btnPrimario} onClick={() => setAperto({})}><Plus size={15} /> Nuovo regime</button></div>}
      {!lista ? <div style={s.vuoto}>Caricamento…</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {lista.map(r => (
            <div key={r.id} style={{ ...s.boxRicevute, flex: 'none', opacity: r.attivo ? 1 : 0.6 }}>
              <div style={s.testaCol}>
                <div style={{ fontWeight: 800 }}>{r.nome} {!r.attivo && <span style={{ ...s.tag, ...s.tagGrigio }}>SPENTO</span>}</div>
                {puoMod && <div style={{ display: 'flex', gap: 4 }}>
                  {!r.attivo && <button style={s.btn} onClick={() => riaccendi(r)}>Riaccendi</button>}
                  <button style={s.icona} title="Modifica" onClick={() => setAperto(r)}><Pencil size={15} /></button>
                  {r.attivo && puoFare(user, 'ricevute_asd', 'delete') && <button style={{ ...s.icona, color: '#C0392B' }} title="Elimina" onClick={() => elimina(r)}><Trash2 size={15} /></button>}
                </div>}
              </div>
              <div style={{ fontSize: 14, fontStyle: 'italic', color: 'var(--text-muted)', whiteSpace: 'pre-line' }}>{r.testo}</div>
            </div>
          ))}
        </div>
      )}
      {aperto && <InFinestra attiva onChiudi={() => setAperto(null)} larghezza={680}>
        <EditorRegime regime={aperto.id ? aperto : null} onChiudi={() => setAperto(null)} onSalvato={() => { setAperto(null); carica(); }} />
      </InFinestra>}
    </>
  );
}

function EditorRegime({ regime, onChiudi, onSalvato }) {
  const [nome, setNome] = useState(regime?.nome || '');
  const [testo, setTesto] = useState(regime?.testo || '');
  const [err, setErr] = useState('');
  const salva = async (e) => {
    e.preventDefault(); setErr('');
    try { regime ? await api.put(`/ricevute-asd/regimi/${regime.id}`, { nome, testo, attivo: regime.attivo }) : await api.post('/ricevute-asd/regimi', { nome, testo }); onSalvato(); }
    catch (x) { setErr(errore(x, 'Non salvato')); }
  };
  return (
    <form style={s.finestra} onSubmit={salva}>
      <div style={s.testaFin}><div style={{ fontSize: 18, fontWeight: 800 }}>{regime ? 'Modifica regime' : 'Nuovo regime IVA'}</div><button type="button" style={s.icona} onClick={onChiudi}><X size={18} /></button></div>
      <label style={s.campo}><span style={s.etichetta}>NOME (QUELLO CHE SCEGLI NELLA RICEVUTA)</span>
        <input style={s.input} value={nome} maxLength={120} onChange={e => setNome(e.target.value)} placeholder="es. Contributo liberale (art. 2)" /></label>
      <label style={s.campo}><span style={s.etichetta}>TESTO STAMPATO SULLA RICEVUTA</span>
        <textarea style={{ ...s.input, minHeight: 140 }} value={testo} maxLength={1500} onChange={e => setTesto(e.target.value)} /></label>
      <div style={s.dim}>Puoi usare <code>{'{{affiliazione}}'}</code>: diventa la federazione/ente in comune scritta sulla ricevuta.</div>
      {err && <div style={s.err}>{err}</div>}
      <div style={s.piede}><button type="button" style={s.btn} onClick={onChiudi}>Annulla</button><button style={s.btnPrimario}>Salva</button></div>
    </form>
  );
}

function SegnaPagata({ r, onChiudi, onFatto }) {
  const [metodo, setMetodo] = useState('BONIFICO');
  const [data, setData] = useState(new Date().toISOString().slice(0, 10));
  const [err, setErr] = useState('');
  const salva = async (e) => {
    e.preventDefault(); setErr('');
    try { await api.put(`/ricevute-asd/ricevute/${r.id}/pagamento`, { pagata: true, metodo_pagamento: metodo, data_pagamento: data }); onFatto(); }
    catch (x) { setErr(errore(x, 'Non salvato')); }
  };
  return (
    <form style={s.finestra} onSubmit={salva}>
      <div style={s.testaFin}><div style={{ fontSize: 18, fontWeight: 800 }}>Ricevuta {r.numero_testo} pagata</div><button type="button" style={s.icona} onClick={onChiudi}><X size={18} /></button></div>
      <div style={s.dim}>{euro(r.totale)} · dal PDF sparisce la scritta NON PAGATA.</div>
      <label style={s.campo}><span style={s.etichetta}>COME</span>
        <select style={s.input} value={metodo} onChange={e => setMetodo(e.target.value)}>{METODI.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></label>
      <label style={s.campo}><span style={s.etichetta}>QUANDO</span>
        <input type="date" style={s.input} value={data} max={new Date().toISOString().slice(0, 10)} onChange={e => setData(e.target.value)} /></label>
      {err && <div style={s.err}>{err}</div>}
      <div style={s.piede}><button type="button" style={s.btn} onClick={onChiudi}>Annulla</button><button style={s.btnPrimario}>Segna pagata</button></div>
    </form>
  );
}

const s = {
  pagina: { padding: '4px 0 40px', color: 'var(--text)' },
  titolo: { fontFamily: "'Barlow Condensed'", fontSize: 'var(--font-size-title)', fontWeight: 800, letterSpacing: '2px', margin: '2px 0 6px' },
  sub: { color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)', marginBottom: 14, maxWidth: 820 },
  p: { fontSize: 'var(--font-size-sm)', lineHeight: 1.55, margin: '0 0 6px' },
  schede: { display: 'inline-flex', gap: 4, background: 'var(--bg-input)', padding: 4, borderRadius: 12, marginBottom: 16, flexWrap: 'wrap' },
  scheda: { border: 'none', background: 'transparent', padding: '9px 16px', borderRadius: 9, fontWeight: 600, fontSize: 14, color: 'var(--text-muted)', cursor: 'pointer' },
  schedaOn: { background: 'var(--bg-card)', color: 'var(--text)', fontWeight: 700, boxShadow: '0 1px 3px rgba(0,0,0,.08)' },
  barra: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 },
  cerca: { flex: '1 1 260px', display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--border2)', borderRadius: 12, padding: '0 12px', background: 'var(--bg-card)', color: 'var(--text-muted)', minHeight: 42 },
  cercaInput: { flex: 1, border: 'none', outline: 'none', background: 'transparent', color: 'var(--text)', fontSize: 15, minWidth: 0 },
  banner: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 14, padding: '11px 14px', fontSize: 14, marginBottom: 14 },
  bannerAvviso: { background: '#FFF1E0', borderColor: '#FBD5A8', color: '#8A3B00' },
  griglia: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 },
  card: { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 18, padding: 18, textAlign: 'left', cursor: 'pointer', color: 'var(--text)', display: 'flex', flexDirection: 'column', gap: 6, minHeight: 120 },
  nessuna: { background: 'var(--bg-card)', border: '1px dashed var(--border2)', borderRadius: 16, padding: 30, display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center', textAlign: 'center' },
  briciole: { fontSize: 13, marginBottom: 10, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 },
  colonne: { display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' },
  boxDati: { flex: '1 1 280px', maxWidth: 380, minWidth: 0, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: 16, display: 'flex', flexDirection: 'column', gap: 4 },
  boxRicevute: { flex: '999 1 480px', minWidth: 0, background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 },
  testaCol: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 4 },
  rigaRic: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 12px', borderRadius: 12, background: 'var(--bg)' },
  anteprimaIva: { fontSize: 13, fontStyle: 'italic', color: 'var(--text-muted)', background: 'var(--bg-input)', borderRadius: 10, padding: '9px 12px', lineHeight: 1.45 },
  rigaEdit: { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' },
  finestra: { padding: 20, display: 'flex', flexDirection: 'column', gap: 12, color: 'var(--text)' },
  testaFin: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 },
  campi: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 },
  campo: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 },
  etichetta: { fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', color: 'var(--text-muted)', textTransform: 'uppercase' },
  input: { width: '100%', border: '1px solid var(--border2)', borderRadius: 12, padding: '10px 12px', fontSize: 15, color: 'var(--text)', background: 'var(--bg-card)', boxSizing: 'border-box', fontFamily: 'inherit' },
  piede: { display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' },
  btn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 13px', borderRadius: 999, border: '1px solid var(--border2)', background: 'var(--bg-input)', color: 'var(--text)', fontSize: 13, fontWeight: 700, cursor: 'pointer', minHeight: 36 },
  btnPrimario: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 15px', borderRadius: 999, border: '1px solid #C24B08', background: '#C24B08', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', minHeight: 38 },
  icona: { border: 'none', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer', padding: 6, display: 'inline-flex', borderRadius: 8 },
  link: { border: 'none', background: 'none', color: '#C24B08', fontWeight: 700, cursor: 'pointer', fontSize: 13, display: 'inline-flex', gap: 4, alignItems: 'center', padding: '4px 0' },
  tag: { fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 6, whiteSpace: 'nowrap' },
  tagBlu: { color: '#1D4FA8', background: '#E6EEFB' },
  tagArancio: { color: '#8A3B00', background: '#FFF1E0' },
  tagVerde: { color: '#1F7A45', background: '#E7F6EC' },
  tagGrigio: { color: 'var(--text-muted)', background: 'var(--bg-input)' },
  dim: { color: 'var(--text-muted)', fontSize: 13 },
  vuoto: { color: 'var(--text-muted)', padding: 20 },
  err: { background: '#FDE8E8', color: '#9B1C1C', borderRadius: 12, padding: '10px 12px', fontSize: 14, marginBottom: 10 },
};
